import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, ConfigProvider, Input, Tag } from "antd";
import type { SessionData } from "./types";
import { NewRoadmapForm } from "../../../components/NewRoadmapForm/NewRoadmapForm";
import {
  Plus,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Trash2,
  RotateCcw,
  X,
  Pencil,
  Check,
  UserCog,
  Users,
  Search,
  MapIcon,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "./rb-globals.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

const phaseLabels: Record<number, string> = {
  2: "Data Gathering",
  3: "Synthesis",
  4: "Peak Ambition",
  5: "Objectives",
  6: "Gap Filling",
  7: "Draft Ready",
};

const phaseTagColor: Record<number, string | undefined> = {
  2: undefined,
  3: "orange",
  4: "purple",
  5: "purple",
  6: "geekblue",
  7: "success",
};

function countFilledBlocks(blocks: SessionData["buildingBlocks"]): number {
  return Object.values(blocks).filter((b: any) => b.status !== "empty").length;
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function daysUntilPurge(deletedAt: string): number {
  const deleted = new Date(deletedAt).getTime();
  const purgeAt = deleted + 30 * 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / (24 * 60 * 60 * 1000)));
}

interface NavItemProps {
  label: string;
  count: number;
  active: boolean;
  expanded: boolean;
  onNavigate: () => void;
  onToggle: (e: React.MouseEvent) => void;
}

const NavItem: React.FC<NavItemProps> = ({ label, count, active, expanded, onNavigate, onToggle }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 12px",
      borderRadius: 6,
      cursor: "pointer",
      background: active ? "#f1f0fe" : "transparent",
      marginBottom: 2,
    }}
  >
    <button
      onClick={onNavigate}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        padding: 0,
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        color: active ? "#2c1561" : "#475569",
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {count > 0 && (
        <span
          style={{
            fontSize: 11,
            background: active ? "#e8e6fb" : "#f1f5f9",
            color: active ? "#2c1561" : "#64748b",
            borderRadius: 999,
            padding: "1px 7px",
            fontWeight: 500,
          }}
        >
          {count}
        </span>
      )}
    </button>
    <button
      onClick={onToggle}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 2,
        color: "#94a3b8",
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
      }}
      title={expanded ? "Collapse" : "Expand"}
    >
      {expanded
        ? <ChevronUp style={{ width: 12, height: 12 }} />
        : <ChevronDown style={{ width: 12, height: 12 }} />}
    </button>
  </div>
);

export function MyRoadmaps() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [deletedSessions, setDeletedSessions] = useState<SessionData[]>([]);
  const [advisingSessions, setAdvisingSessions] = useState<{ id: string; companyName: string; updatedAt: string | null }[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [hardDeletingId, setHardDeletingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [myExpanded, setMyExpanded] = useState(true);
  const [teamExpanded, setTeamExpanded] = useState(true);
  const [sharedExpanded, setSharedExpanded] = useState(true);
  const [advisingExpanded, setAdvisingExpanded] = useState(true);
  const [trashExpanded, setTrashExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState<"my" | "team" | "shared" | "advising" | "deleted">("my");

  // New roadmap form
  const [showNewForm, setShowNewForm] = useState(false);

  // Section refs for scroll navigation
  const myRef = useRef<HTMLDivElement>(null);
  const teamRef = useRef<HTMLDivElement>(null);
  const sharedRef = useRef<HTMLDivElement>(null);
  const advisingRef = useRef<HTMLDivElement>(null);
  const deletedRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const loadSessions = async () => {
    try {
      const [sessRes, delRes, advRes] = await Promise.all([
        fetch(`${SERVER_URL}/api/rb/sessions`, { credentials: "include" }),
        fetch(`${SERVER_URL}/api/rb/sessions/deleted/list`, { credentials: "include" }),
        fetch(`${SERVER_URL}/api/rb/advisor/sessions`, { credentials: "include" }),
      ]);
      if (sessRes.ok) setSessions(await sessRes.json());
      if (delRes.ok) setDeletedSessions(await delRes.json());
      if (advRes.ok) setAdvisingSessions(await advRes.json());
    } catch (e) {
      console.error("Failed to load sessions", e);
    }
  };

  useEffect(() => { loadSessions(); }, []);

  const ownedSessions = sessions.filter((s) => !s.collaboratorRole);
  const teamSessions = sessions.filter((s) => s.collaboratorRole === "team_member");
  const sharedSessions = sessions.filter((s) => !!s.collaboratorRole && s.collaboratorRole !== "team_member");

  const q = searchQuery.toLowerCase().trim();
  const filteredOwned = q ? ownedSessions.filter((s) => s.companyName.toLowerCase().includes(q)) : ownedSessions;
  const filteredTeam = q ? teamSessions.filter((s) => s.companyName.toLowerCase().includes(q)) : teamSessions;
  const filteredShared = q ? sharedSessions.filter((s) => s.companyName.toLowerCase().includes(q)) : sharedSessions;
  const filteredAdvising = q ? advisingSessions.filter((s) => s.companyName.toLowerCase().includes(q)) : advisingSessions;
  const filteredDeleted = q ? deletedSessions.filter((s) => s.companyName.toLowerCase().includes(q)) : deletedSessions;

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>, section: typeof activeSection) => {
    setActiveSection(section);
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleRenameStart = (e: React.MouseEvent, session: SessionData) => {
    e.stopPropagation();
    setRenamingId(session.id);
    setRenameValue(session.companyName);
  };

  const handleRenameSave = async (sessionId: string) => {
    const trimmed = renameValue.trim();
    const current = sessions.find((s) => s.id === sessionId);
    if (!trimmed || trimmed === current?.companyName) { setRenamingId(null); return; }
    try {
      const res = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/rename`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyName: trimmed }),
        credentials: "include",
      });
      if (res.ok) setSessions((prev) => prev.map((s) => s.id === sessionId ? { ...s, companyName: trimmed } : s));
    } catch (e) { console.error("Failed to rename", e); }
    finally { setRenamingId(null); }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}`, { method: "DELETE", credentials: "include" });
      setSessions((prev) => prev.filter((s) => s.id !== id));
      loadSessions();
    } finally { setDeletingId(null); }
  };

  const handleRestore = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setRestoringId(id);
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/restore`, { method: "POST", credentials: "include" });
      loadSessions();
    } finally { setRestoringId(null); }
  };

  const handleHardDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm("Permanently delete this roadmap? This cannot be undone.")) return;
    setHardDeletingId(id);
    try {
      await fetch(`${SERVER_URL}/api/rb/sessions/${id}/hard`, { method: "DELETE", credentials: "include" });
      setDeletedSessions((prev) => prev.filter((s) => s.id !== id));
    } finally { setHardDeletingId(null); }
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
      <div className="rb-root" style={{ height: "100%", display: "flex", overflow: "hidden" }}>

        {/* ── Left nav ── */}
        <div
          style={{
            width: 200,
            flexShrink: 0,
            borderRight: "1px solid #f1f5f9",
            height: "100%",
            overflowY: "auto",
            padding: "16px 8px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Search */}
          <div style={{ padding: "0 4px 10px" }}>
            <Input
              prefix={<Search style={{ width: 12, height: 12, color: "#94a3b8" }} />}
              placeholder="Search roadmaps"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontSize: 12 }}
              allowClear
            />
          </div>

          <p style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8", padding: "0 12px", margin: "0 0 4px" }}>
            Roadmaps
          </p>

          <NavItem
            label="My Roadmaps"
            count={ownedSessions.length}
            active={activeSection === "my"}
            expanded={myExpanded}
            onNavigate={() => scrollToSection(myRef, "my")}
            onToggle={(e) => { e.stopPropagation(); setMyExpanded((v) => !v); }}
          />

          {teamSessions.length > 0 && (
            <NavItem
              label="Team Roadmaps"
              count={teamSessions.length}
              active={activeSection === "team"}
              expanded={teamExpanded}
              onNavigate={() => scrollToSection(teamRef, "team")}
              onToggle={(e) => { e.stopPropagation(); setTeamExpanded((v) => !v); }}
            />
          )}

          {sharedSessions.length > 0 && (
            <NavItem
              label="Shared With Me"
              count={sharedSessions.length}
              active={activeSection === "shared"}
              expanded={sharedExpanded}
              onNavigate={() => scrollToSection(sharedRef, "shared")}
              onToggle={(e) => { e.stopPropagation(); setSharedExpanded((v) => !v); }}
            />
          )}

          {advisingSessions.length > 0 && (
            <NavItem
              label="I'm Advising"
              count={advisingSessions.length}
              active={activeSection === "advising"}
              expanded={advisingExpanded}
              onNavigate={() => scrollToSection(advisingRef, "advising")}
              onToggle={(e) => { e.stopPropagation(); setAdvisingExpanded((v) => !v); }}
            />
          )}

          {deletedSessions.length > 0 && (
            <>
              <div style={{ height: 1, background: "#f1f5f9", margin: "8px 12px" }} />
              <NavItem
                label="Deleted"
                count={deletedSessions.length}
                active={activeSection === "deleted"}
                expanded={trashExpanded}
                onNavigate={() => scrollToSection(deletedRef, "deleted")}
                onToggle={(e) => { e.stopPropagation(); setTrashExpanded((v) => !v); }}
              />
            </>
          )}
        </div>

        {/* ── Main content ── */}
        <div ref={scrollContainerRef} style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>
          {/* Page header */}
          <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <MapIcon style={{ width: 18, height: 18, color: "#2c1561" }} />
              <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "#0f172a", fontFamily: '"Albert Sans", sans-serif' }}>
                My Roadmaps
              </h1>
            </div>
            <Button
              type="primary"
              size="small"
              icon={<Plus style={{ width: 13, height: 13 }} />}
              onClick={() => setShowNewForm((v) => !v)}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              New Roadmap
            </Button>
          </div>

          {/* Inline new roadmap form */}
          <AnimatePresence>
            {showNewForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: "hidden", borderBottom: "1px solid #f1f5f9" }}
              >
                <div style={{ padding: "16px 24px" }}>
                  <NewRoadmapForm onBack={() => setShowNewForm(false)} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── My Roadmaps section ── */}
          <div ref={myRef} style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: myExpanded ? 16 : 0, cursor: "pointer" }} onClick={() => setMyExpanded((v) => !v)}>
              <h2 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>My Roadmaps</h2>
              <span style={{ fontSize: 11, background: "#f1f5f9", color: "#64748b", borderRadius: 999, padding: "1px 7px", fontWeight: 500 }}>{filteredOwned.length}</span>
              <span style={{ marginLeft: "auto", color: "#94a3b8" }}>
                {myExpanded ? <ChevronUp style={{ width: 13, height: 13 }} /> : <ChevronDown style={{ width: 13, height: 13 }} />}
              </span>
            </div>

            <AnimatePresence initial={false}>
              {myExpanded && (
                <motion.div
                  key="my-grid"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: "hidden" }}
                >
                  {filteredOwned.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "32px 16px" }}>
                      <p className="text-sm text-muted-foreground" style={{ marginBottom: 12 }}>
                        {q ? "No roadmaps match your search." : "No roadmaps yet."}
                      </p>
                      {!q && (
                        <Button type="primary" size="small" icon={<Plus style={{ width: 12, height: 12 }} />} onClick={() => setShowNewForm(true)}>
                          Create your first roadmap
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredOwned.map((session, i) => {
                        const filled = countFilledBlocks(session.buildingBlocks);
                        const phase = session.currentPhase ?? 2;
                        const phaseName = phaseLabels[phase] ?? "In Progress";
                        const progressPct = Math.round((filled / 7) * 100);
                        return (
                          <motion.div key={session.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                            <Card styles={{ body: { padding: 18 } }} className="hover:shadow-md transition-all duration-200 cursor-pointer" onClick={() => navigate(`/roadmap/build_roadmap/${session.id}`)}>
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="min-w-0 flex-1">
                                  {renamingId === session.id ? (
                                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        className="text-sm font-medium leading-tight text-foreground border-b-2 border-primary bg-transparent outline-none flex-1 min-w-0 py-0"
                                        style={{ fontFamily: '"Albert Sans", sans-serif' }}
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onBlur={() => handleRenameSave(session.id)}
                                        onKeyDown={(e) => { if (e.key === "Enter") handleRenameSave(session.id); if (e.key === "Escape") setRenamingId(null); }}
                                        autoFocus
                                      />
                                      <button className="icon-button-muted shrink-0" onMouseDown={(e) => { e.preventDefault(); handleRenameSave(session.id); }}>
                                        <Check className="icon-14" />
                                      </button>
                                    </div>
                                  ) : (
                                    <h3 className="text-sm font-medium truncate leading-tight text-foreground" style={{ margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>
                                      {session.companyName}
                                    </h3>
                                  )}
                                  {session.domain && !session.linkType && (
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">{session.domain}</p>
                                  )}
                                </div>
                                <div style={{ flexShrink: 0 }}>
                                  {session.linkType === "solo_contribution"
                                    ? <Tag color="purple" style={{ margin: 0 }}>Solo</Tag>
                                    : <Tag color={phaseTagColor[phase]} style={{ margin: 0, fontSize: 10 }}>{phaseName}</Tag>}
                                </div>
                              </div>
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                                  <span>Blocks</span><span>{filled} / 7</span>
                                </div>
                                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
                                </div>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">{relativeTime(session.updatedAt)}</span>
                                <div className="flex items-center gap-1">
                                  <button onClick={(e) => handleRenameStart(e, session)} className="icon-button-muted" title="Rename"><Pencil className="icon-14" /></button>
                                  <button onClick={(e) => handleDelete(e, session.id)} disabled={deletingId === session.id} className="icon-button-muted" title="Move to trash"><Trash2 className="icon-14" /></button>
                                  <span className="text-xs font-medium text-primary/60 flex items-center gap-0.5">Open <ChevronRight className="h-3.5 w-3.5" /></span>
                                </div>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── Team Roadmaps ── */}
          {(teamSessions.length > 0 || (q && filteredTeam.length > 0)) && (
            <div ref={teamRef} style={{ padding: "0 24px 20px", borderTop: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0", cursor: "pointer" }} onClick={() => setTeamExpanded((v) => !v)}>
                <Users style={{ width: 14, height: 14, color: "#64748b" }} />
                <h2 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>Team Roadmaps</h2>
                <span style={{ fontSize: 11, background: "#f1f5f9", color: "#64748b", borderRadius: 999, padding: "1px 7px", fontWeight: 500 }}>{filteredTeam.length}</span>
                <span style={{ marginLeft: "auto", color: "#94a3b8" }}>
                  {teamExpanded ? <ChevronUp style={{ width: 13, height: 13 }} /> : <ChevronDown style={{ width: 13, height: 13 }} />}
                </span>
              </div>
              <AnimatePresence initial={false}>
                {teamExpanded && (
                  <motion.div key="team-grid" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredTeam.map((session, i) => {
                        const filled = countFilledBlocks(session.buildingBlocks);
                        const phase = session.currentPhase ?? 2;
                        const phaseName = phaseLabels[phase] ?? "In Progress";
                        const progressPct = Math.round((filled / 7) * 100);
                        return (
                          <motion.div key={session.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                            <Card styles={{ body: { padding: 18 } }} className="hover:shadow-md transition-all duration-200 cursor-pointer" onClick={() => navigate(`/roadmap/build_roadmap/${session.id}`)}>
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="min-w-0 flex-1">
                                  <h3 className="text-sm font-medium truncate leading-tight text-foreground" style={{ margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>
                                    {session.companyName}
                                  </h3>
                                  {session.domain && (
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">{session.domain}</p>
                                  )}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                                  <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>Team Member</Tag>
                                  <Tag color={phaseTagColor[phase]} style={{ margin: 0, fontSize: 10 }}>{phaseName}</Tag>
                                </div>
                              </div>
                              <div className="mb-3">
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                                  <span>Blocks</span><span>{filled} / 7</span>
                                </div>
                                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
                                </div>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">{relativeTime(session.updatedAt)}</span>
                                <span className="text-xs font-medium text-primary/60 flex items-center gap-0.5">Open <ChevronRight className="h-3.5 w-3.5" /></span>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ── Shared With Me ── */}
          {(sharedSessions.length > 0 || (q && filteredShared.length > 0)) && (
            <div ref={sharedRef} style={{ padding: "0 24px 20px", borderTop: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0", cursor: "pointer" }} onClick={() => setSharedExpanded((v) => !v)}>
                <Users style={{ width: 14, height: 14, color: "#64748b" }} />
                <h2 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>Shared With Me</h2>
                <span style={{ fontSize: 11, background: "#f1f5f9", color: "#64748b", borderRadius: 999, padding: "1px 7px", fontWeight: 500 }}>{filteredShared.length}</span>
                <span style={{ marginLeft: "auto", color: "#94a3b8" }}>
                  {sharedExpanded ? <ChevronUp style={{ width: 13, height: 13 }} /> : <ChevronDown style={{ width: 13, height: 13 }} />}
                </span>
              </div>
              <AnimatePresence initial={false}>
                {sharedExpanded && (
                  <motion.div key="shared-grid" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredShared.map((session, i) => {
                        const filled = countFilledBlocks(session.buildingBlocks);
                        const progressPct = Math.round((filled / 7) * 100);
                        const isContributor = session.collaboratorRole === "solo_contributor";
                        const isSubmitted = isContributor && !!session.submittedAt;
                        const roleLabel = (session.collaboratorRole === "view" || session.collaboratorRole === "comment") ? "Reviewer" : isSubmitted ? "Submitted ✓" : "Contributor";
                        const roleColor = (session.collaboratorRole === "view" || session.collaboratorRole === "comment") ? "default" : isSubmitted ? "success" : "purple";
                        return (
                          <motion.div key={session.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                            <Card styles={{ body: { padding: 18 } }} className="hover:shadow-md transition-all duration-200 cursor-pointer" onClick={() => navigate(`/roadmap/build_roadmap/${session.id}`)}>
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <h3 className="font-medium text-sm leading-snug line-clamp-2" style={{ margin: 0 }}>{session.companyName}</h3>
                                <Tag color={roleColor} className="text-xs shrink-0">{roleLabel}</Tag>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <div className="flex-1 bg-slate-100 rounded-full h-1">
                                  <div className="bg-primary h-1 rounded-full" style={{ width: `${progressPct}%` }} />
                                </div>
                                <span className="text-xs text-muted-foreground">{filled}/7</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">Updated {relativeTime(session.updatedAt)}</p>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ── Advising ── */}
          {(advisingSessions.length > 0 || (q && filteredAdvising.length > 0)) && (
            <div ref={advisingRef} style={{ padding: "0 24px 20px", borderTop: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0", cursor: "pointer" }} onClick={() => setAdvisingExpanded((v) => !v)}>
                <UserCog style={{ width: 14, height: 14, color: "#64748b" }} />
                <h2 style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>Roadmaps I'm Advising</h2>
                <span style={{ fontSize: 11, background: "#f1f5f9", color: "#64748b", borderRadius: 999, padding: "1px 7px", fontWeight: 500 }}>{filteredAdvising.length}</span>
                <span style={{ marginLeft: "auto", color: "#94a3b8" }}>
                  {advisingExpanded ? <ChevronUp style={{ width: 13, height: 13 }} /> : <ChevronDown style={{ width: 13, height: 13 }} />}
                </span>
              </div>
              <AnimatePresence initial={false}>
                {advisingExpanded && (
                  <motion.div key="advising-grid" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredAdvising.map((session, i) => (
                        <motion.div key={session.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                          <Card styles={{ body: { padding: 18 } }} className="hover:shadow-md transition-all duration-200 cursor-pointer" onClick={() => navigate(`/roadmap/build_roadmap/${session.id}`)}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="text-sm font-medium truncate leading-tight text-foreground" style={{ margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>{session.companyName}</h3>
                              <Tag color="purple" className="shrink-0" style={{ fontSize: 10, margin: 0 }}>Advisor</Tag>
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <span className="text-xs text-muted-foreground">{session.updatedAt ? `Updated ${relativeTime(session.updatedAt)}` : ""}</span>
                              <span className="text-xs font-medium text-primary/60 flex items-center gap-0.5">Open <ChevronRight className="h-3.5 w-3.5" /></span>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ── Deleted ── */}
          {deletedSessions.length > 0 && (
            <div ref={deletedRef} style={{ padding: "0 24px 20px", borderTop: "1px solid #f1f5f9" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0", cursor: "pointer" }} onClick={() => setTrashExpanded((v) => !v)}>
                <Trash2 style={{ width: 13, height: 13, color: "#94a3b8" }} />
                <h2 style={{ fontSize: 13, fontWeight: 500, color: "#64748b", margin: 0, fontFamily: '"Albert Sans", sans-serif' }}>Deleted</h2>
                <Tag color="error" style={{ margin: 0, fontSize: 10 }}>{filteredDeleted.length}</Tag>
                <span style={{ marginLeft: "auto", color: "#94a3b8" }}>
                  {trashExpanded ? <ChevronUp style={{ width: 13, height: 13 }} /> : <ChevronDown style={{ width: 13, height: 13 }} />}
                </span>
              </div>
              <AnimatePresence initial={false}>
                {trashExpanded && (
                  <motion.div key="trash-grid" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: "hidden" }}>
                    <p className="text-sm text-muted-foreground mb-4">Deleted roadmaps are permanently removed after 30 days</p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredDeleted.map((session, i) => {
                        const days = daysUntilPurge(session.deletedAt ?? "");
                        return (
                          <motion.div key={session.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                            <Card styles={{ body: { padding: 18 } }} className="opacity-70">
                              <div className="flex items-start justify-between gap-2 mb-3">
                                <h3 className="font-medium text-sm line-clamp-2 text-muted-foreground" style={{ margin: 0 }}>{session.companyName}</h3>
                                <Tag color="error" style={{ margin: 0 }}>{days}d left</Tag>
                              </div>
                              <div className="flex items-center justify-between mt-2">
                                <span className="text-xs text-muted-foreground">Deleted {session.deletedAt ? relativeTime(session.deletedAt) : ""}</span>
                                <div className="flex items-center gap-1">
                                  <Button type="text" size="small" icon={<RotateCcw style={{ width: 11, height: 11 }} />} loading={restoringId === session.id} onClick={(e) => handleRestore(e, session.id)}>Restore</Button>
                                  <Button type="text" size="small" danger icon={<X style={{ width: 11, height: 11 }} />} loading={hardDeletingId === session.id} onClick={(e) => handleHardDelete(e, session.id)}>Delete forever</Button>
                                </div>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </ConfigProvider>
  );
}
